// Packages
import { View, NavigateAction } from 'react-big-calendar';
// Modules
import storage from 'modules/storage';
// Configs
import dateConfig from 'configs/dateConfig';
// Types
import { TGoogleLibraries } from 'types/appTypes';

export const IS_AM_PM_TIME_FORMAT = storage.getData('timeGoUse12Hours');

export const EMAIL_PATTERN =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+\.)+[a-zA-Z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]{2,}))$/;

export const EMAIL_REGEX =
  /^(?=[^@]{1,64}@)[a-zA-Z0-9+_-]+(\.[a-zA-Z0-9+_-]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const CSV_EXAMPLE_LINK =
  'https://timego-public.s3.us-east-2.amazonaws.com/import-csv-example-v1.csv';

export const DATE_FORMAT = 'DD-MMM-YYYY';
// Shown first; the rest only parse typed text, so "12oct2026" or "12 oct 2026" works without the
// dashes. The month stays a word: digits only would be ambiguous ("10112026" is 10 Nov or Oct 11).
export const DATE_INPUT_FORMATS = [DATE_FORMAT, 'DDMMMYYYY', 'DD MMM YYYY'];
export const DATE_FORMAT_STANDARD = 'YYYY-MM-DD';
export const TABLE_PAGINATION_THRESHOLD = 10;

export const MAX_INVOICE_AMOUNT = 999999999999.99;

// Upper limits for numeric form inputs (#258). Decimal cap stays < JS safe integer
// (no precision loss); integer cap stays < PostgreSQL int4 max (2,147,483,647).
export const MAX_DECIMAL_INPUT = 999999999999.99;
export const MAX_INTEGER_INPUT = 999999999;

export const NUMBER_DATE_FORMAT = 'YYYY-MM-DD';
export const LONG_DATE_FORMAT = 'dddd, MMM DD';
export const SHORT_DATE_FORMAT = 'MMM DD';
export const TIME_FORMAT = dateConfig.appTimeFormat;
export const USE_12_HOURS = dateConfig.shouldUse12Hours;
// A time of day as the server sends and takes it, whatever the user's 12 or 24 hours choice.
export const SERVER_TIME_OF_DAY_FORMAT = 'HH:mm';

export const DND_TASK_TYPE = 'task';
export const DND_COMPONENT_TYPE = 'formComponent';

// A 409 the page answers itself (a banner), so the generic error toast stays away.
export const TODO_VERSION_CONFLICT = 'TODO_CHANGED_BY_SOMEONE_ELSE';

export const NOT_FOUND = [
  'COMPANY_NOT_FOUND',
  'CONTACT_NOT_FOUND',
  'LOCATION_NOT_FOUND',
  'JOB_NOT_FOUND',
  'EMPLOYEE_NOT_FOUND',
  'PERFORMANCE_LOG_NOT_FOUND',
  'LEAVE_REQUEST_NOT_FOUND',
  'EMPLOYEE_RATE_NOT_FOUND',
  'EMPLOYEE_FILE_NOT_FOUND',
  'FORM_NOT_FOUND',
  'TODO_NOT_FOUND',
  'JOB_LOG_NOT_FOUND',
  'JOB_EXPENSE_NOT_FOUND',
  'EQUIPMENT_NOT_FOUND',
  'EQUIPMENT_LOG_NOT_FOUND',
  'RECURRING_INVOICE_NOT_FOUND',
  'CHECKPOINT_NOT_FOUND',
  'INCIDENT_NOT_FOUND',
  'PARKING_ISSUE_NOT_FOUND',
  'CHECKPOINT_SCAN_NOT_FOUND',
  'FORM_SCHEDULED_REPORT_NOT_FOUND',
  'CONTACT_LOG_NOT_FOUND',
];

export const ROLES = {
  admin: 'admin',
  business_admin: 'business_admin',
  business_company: 'business_company',
  manager: 'manager',
  employee: 'employee',
  support: 'support',
};

export const DEFAULT_MAP_ZOOM = 15;

/** Lowest zoom level any map may reach — below this the world repeats horizontally */
export const MIN_MAP_ZOOM = 2;

/** Debounce (ms) before refetching user on visibility change to avoid duplicate requests */
export const SUBSCRIPTION_REFRESH_DEBOUNCE_MS = 2000;

/** Document visibility state (Page Visibility API) */
export enum DocumentVisibilityState {
  Visible = 'visible',
  Hidden = 'hidden',
  Prerender = 'prerender',
}

// Palo Alto, California
export const DEFAULT_MAP_CENTER = {
  lat: 37.43940279972828,
  lng: -122.14167226540688,
};

export const CIRCLE_OPTIONS = {
  fillOpacity: 0.1,
  fillColor: '#1493ff',
  strokeColor: '#1B96FF',
  strokeOpacity: 0.5,
  strokeWeight: 1.2,
};

export const MARKS_FOR_CIRCLE_SLIDER = {
  50: '50m',
  2500: '2500m',
};

export const MIN_SLIDER_VALUE = 50;
export const MAX_SLIDER_VALUE = 2500;

export const DEFAULT_MAP_TYPE_ID = 'hybrid';

export const GEOFENCE_TYPE = {
  CIRCLE: 'circle',
  POLYGON: 'polygon',
} as const;

export const MIN_POLYGON_POINTS = 3;
export const MAX_POLYGON_POINTS = 15;
export const DEFAULT_GEOREFERENCE_RADIUS = 250;
export const MIN_POLYGON_AREA = 100; // m²
export const MIN_COMPACTNESS_RATIO = 0.05;
export const SQ_METERS_IN_HECTARE = 10_000;

export const POLYGON_OPTIONS = {
  fillOpacity: 0.15,
  fillColor: '#1493ff',
  strokeColor: '#1B96FF',
  strokeOpacity: 0.8,
  strokeWeight: 2,
  clickable: false,
};

export const VERTEX_MARKER_OPTIONS: google.maps.CircleOptions = {
  fillOpacity: 1,
  fillColor: '#1B96FF',
  strokeColor: '#fff',
  strokeWeight: 2,
  clickable: false,
  radius: 14,
};

export const ACTIVE_VERTEX_MARKER_OPTIONS: google.maps.CircleOptions = {
  ...VERTEX_MARKER_OPTIONS,
  fillColor: '#FF6B00',
};

export const MAP_CONTAINER_STYLE = { height: '32rem' };

export const GOOGLE_ADDRESS_PARTS = {
  STREET_NUMBER: 'street_number',
  ROUTE: 'route',
  CITY: 'locality',
  STATE: 'administrative_area_level_1',
  COUNTRY: 'country',
  POSTAL_CODE: 'postal_code',
};

export const PERMISSION_KEYS = {
  ACTIVITY: 'activity',
  SCHEDULE: 'shifts',
  COMPANIES: 'companies',
  CONTACTS: 'contacts',
  CALLBACKS: 'callbacks',
  LOCATIONS: 'locations',
  LOCATIONS_DOCS: 'locationDocs',
  JOBS: 'jobs',
  JOB_RATES: 'jobRates',
  JOB_GROUPS: 'jobGroups',
  JOB_LOGS: 'jobLogs',
  JOB_EXPENSES: 'jobExpenses',
  EMPLOYEES: 'employees',
  EMPLOYEE_GROUPS: 'employeeGroups',
  PERFORMANCE_LOG: 'perfomanceLogs',
  LEAVE_REQUESTS: 'leaveRequests',
  EMPLOYEE_RATES: 'employeeRates',
  TRAININGS_AND_LICENSES: 'trainingsAndLicenses',
  FORMS: 'forms',
  FORM_VIEW: 'formView',
  FORM_RESULTS: 'formResults',
  CHECKPOINTS: 'checkpoints',
  CHECKPOINT_SETTINGS: 'checkpointSettings',
  GPS: 'gps',
  TODOS: 'todos',
  MAP_VIEW: 'mapView',
  CALENDAR: 'calendar',
  REPORTS: 'reports',
  SETTINGS: 'settings',
  PAYMENTS: 'payments',
  PREFERENCES: 'preferences',
  PAYROLL: 'payroll',
  EQUIPMENT: 'equipment',
  INVOICES: 'invoices',
  HELP: 'help',
  FEEDBACK: 'feedback',
  INTEGRATIONS: 'integrations',
  ACTION_LOGS: 'actionLogs',
  IMPLEMENTATION_PLANS: 'implementationPlans',
  LOCATION_RATING: 'locationRatings',
  LOCATION_RATING_MAIN_COMPANY: 'locationRatingMainCompany',
  PAYROLL_ADJUSTMENT: 'payrollAdjustments',
  FORM_SCHEDULE_REPORTS: 'formScheduledReports',
  CONTACT_LOGS: 'contactLogs',
  LABOR_BURDEN: 'laborBurdenCalculations',
  ESTIMATED_REVENUE: 'estimatedRevenue',
  CANDIDATES: 'candidates',
} as const;

export const COMPANY_TYPE = {
  business: 'business',
  myCompany: 'my_company',
};

export const DEF_MANAGER_PERM = {
  implementationPlan: 'create,delete,edit,read',
  checkpoints: 'create,delete,edit,read',
  contacts: 'create,delete,edit,read',
  callbacks: 'read',
  employees: 'create,delete,edit,read',
  employeeGroups: 'read',
  employeeRates: '',
  todos: 'create,delete,edit,read',
  formResults: 'create,delete,edit,read',
  forms: 'create,delete,edit,read',
  formView: 'create,delete,edit,read',
  jobGroups: 'read',
  jobLogs: 'create,delete,edit,read',
  jobRates: '',
  jobExpenses: 'create,delete,edit,read',
  jobs: 'create,delete,edit,read',
  leaveRequests: 'create,delete,edit,read',
  companies: 'create,delete,edit,read',
  locations: 'create,delete,edit,read',
  locationDocs: '',
  perfomanceLogs: 'create,delete,edit,read',
  shifts: 'create,delete,edit,read',
  trainingsAndLicenses: 'create,delete,edit,read',
  equipment: 'read',
  reports: 'read',
  invoices: '',
  help: 'read',
  feedback: 'read',
  integrations: '',
  actionLogs: '',
  locationRatings: '',
  locationRatingMainCompany: 'create,delete,edit,read',
  checkpointSettings: '',
  payrollAdjustments: '',
  formScheduledReports: '',
  contactLogs: 'create,delete,edit,read',
  laborBurdenCalculations: '',
  estimatedRevenue: 'create,delete,edit,read',
  candidates: 'create,delete,edit,read',
};

export const DEF_EMPLOYEE_PERM = {
  implementationPlan: '',
  checkpoints: '',
  contacts: '',
  callbacks: '',
  employees: '',
  employeeGroups: '',
  employeeRates: '',
  todos: '',
  formResults: '',
  forms: '',
  formView: '',
  jobGroups: '',
  jobRates: '',
  jobLogs: '',
  jobs: '',
  leaveRequests: '',
  companies: '',
  locations: '',
  locationDocs: '',
  perfomanceLogs: '',
  shifts: '',
  trainingsAndLicenses: '',
  equipment: '',
  invoices: '',
  help: '',
  feedback: '',
  integrations: '',
  actionLogs: '',
  locationRatings: '',
  locationRatingMainCompany: '',
  checkpointSettings: '',
  payrollAdjustments: '',
  formScheduledReports: '',
  contactLogs: '',
  laborBurdenCalculations: '',
  estimatedRevenue: '',
  candidates: '',
};

export const DEF_ADMIN_PERM = {
  implementationPlan: 'create,delete,edit,read',
  activity: 'read',
  shifts: 'create,read,edit,delete',
  companies: 'create,read,edit,delete',
  contacts: 'create,read,edit,delete',
  callbacks: 'create,read,edit,delete',
  locations: 'create,read,edit,delete',
  locationDocs: 'create,read,edit,delete',
  jobs: 'create,read,edit,delete',
  jobRates: 'create,read,edit,delete',
  jobGroups: 'create,read,edit,delete',
  jobLogs: 'create,read,edit,delete',
  employees: 'create,read,edit,delete',
  employeeGroups: 'create,read,edit,delete',
  perfomanceLogs: 'create,read,edit,delete',
  leaveRequests: 'create,read,edit,delete',
  employeeRates: 'create,read,edit,delete',
  trainingsAndLicenses: 'create,read,edit,delete',
  forms: 'create,read,edit,delete',
  formView: 'create,read,edit,delete',
  formResults: 'create,read,edit,delete',
  checkpoints: 'create,read,edit,delete',
  gps: 'read',
  todos: 'create,read,edit,delete',
  mapView: 'read',
  calendar: 'read',
  reports: 'create,read,edit,delete',
  settings: 'create,read,edit,delete',
  payroll: 'create,read,edit,delete',
  equipment: 'create,read,edit,delete',
  invoices: 'create,read,edit,delete',
  help: 'read',
  feedback: 'create,read,edit,delete',
  integrations: 'create,read,edit,delete',
  actionLogs: 'read,edit',
  locationRatings: '',
  locationRatingMainCompany: 'create,delete,edit,read',
  checkpointSettings: 'create,delete,edit,read',
  payrollAdjustments: 'create,delete,edit,read',
  formScheduledReports: 'read',
  contactLogs: 'create,read,edit,delete',
  laborBurdenCalculations: 'create,read,edit,delete',
  estimatedRevenue: 'create,delete,edit,read',
  candidates: 'create,delete,edit,read',
};

//CALENDAR
export const CALENDAR_FORMATS = {
  DATE_RANGE_MONTH: 'MMMM YYYY',
  DATE_RANGE_WEEK_START: 'DD-MMM',
  DATE_RANGE_WEEK_END: DATE_FORMAT,
  TABLE_HEADER_DATES_MONTH_VIEW: 'dddd',
  TABLE_HEADER_DATES_WEEKS_VIEW: LONG_DATE_FORMAT,
  AGENDA_DATES: 'MMMM DD, dddd',
  // "Wednesday, Aug 13" needs about 110px, so seven of them force the calendar
  // roughly 770px wide — wider than any phone. These compact variants are what
  // let the week and month grids fit the screen instead of overflowing it.
  TABLE_HEADER_DATES_MONTH_VIEW_COMPACT: 'ddd',
  TABLE_HEADER_DATES_WEEKS_VIEW_COMPACT: 'ddd DD',
};

type ViewT = {
  [key: string]: View;
};

export const CALENDAR_VIEWS: ViewT = {
  WEEK: 'week',
  MONTH: 'month',
  AGENDA: 'agenda',
};

type NavigateActionT = {
  [key: string]: NavigateAction;
};

export const CALENDAR_ACTIONS: NavigateActionT = {
  PREV: 'PREV',
  NEXT: 'NEXT',
  TODAY: 'TODAY',
  DATE: 'DATE',
};

// GPS
export const MARKER_COLORS = {
  locationMarkerInRadius: '#1B96FF',
  locationMarkerOutRadius: '#DC251C',
  checkpointsScanMarker: '#65bf73',
  checkInMarker: '#ff8000',
};
export const PLATFORM_TYPES = {
  quickbooksOnline: 'quickbooksOnline',
  xero: 'xero',
};

export const IMAGE_TYPES = ['png', 'jpg', 'jpeg'];
export const DOWNLOAD_FILES = ['ppt', 'pptx', 'xls', 'xlsx', 'doc', 'docx'];
export const IMAGE_TYPES_CHECKPOINTS = [...IMAGE_TYPES, 'gif', 'svg'];

// A CSS variable, not a hex: antd's solid Tag takes any colour string, and this one has to flip with the theme.
export const STATUS_TAG_COLOR = 'var(--text)';

export enum STATUSES {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export const LIBRARIES: TGoogleLibraries = ['places', 'geometry', 'marker'];

// Upload size limits in MB. These MUST mirror the `limits.fileSize` of the matching
// backend multer config — a mismatch means the file is sent and rejected with a raw 413.
export const FILE_MAX_SIZE_MB = 40;
// Feedback is the one endpoint capped lower than the rest (feedback.controller.ts).
export const FEEDBACK_FILE_MAX_SIZE_MB = 10;
